import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Package, Puzzle, Plug, LayoutTemplate,
  Shield, Zap, Rocket, RefreshCw, Star, Code2, Sparkles, LifeBuoy,
  ShoppingCart, Users, GraduationCap, Building2, Newspaper, HeartHandshake,
} from 'lucide-react';
import { setSeoMetadata, generateOrganizationSchema, generateWebsiteSchema, SEO_PRESETS } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Default hero overlay video (overridden by the "Hero Media Assets → Hero
// Overlay Video" setting saved in the admin/dev settings menu).
const DEFAULT_HERO_VIDEO = '/videos/butterfly_alpha.webm';

const getVideoMimeType = (videoUrl) => {
  const normalized = (videoUrl || '').toLowerCase();
  if (normalized.endsWith('.mp4') || normalized.includes('.mp4?')) return 'video/mp4';
  if (normalized.endsWith('.webm') || normalized.includes('.webm?')) return 'video/webm';
  if (normalized.endsWith('.mov') || normalized.includes('.mov?')) return 'video/quicktime';
  if (normalized.endsWith('.ogv') || normalized.includes('.ogv?')) return 'video/ogg';
  return 'video/mp4';
};

// Extension categories we build in-house
const CATEGORIES = [
  {
    id: 'components',
    name: 'Components',
    icon: Package,
    tagline: 'Full-featured apps for Joomla',
    description: 'MVC components that power entire sections of your site — catalogs, directories, memberships, booking systems and more.',
    accent: 'from-blue-600 to-cyan-500',
    count: '18+',
  },
  {
    id: 'modules',
    name: 'Modules',
    icon: Puzzle,
    tagline: 'Drop-in content blocks',
    description: 'Lightweight, position-aware modules — mega menus, sliders, tabbed content and dynamic widgets you can place anywhere.',
    accent: 'from-purple-600 to-indigo-500',
    count: '30+',
  },
  {
    id: 'plugins',
    name: 'Plugins',
    icon: Plug,
    tagline: 'Behavior that runs everywhere',
    description: 'System, content and editor plugins that add SEO, caching, security and automation across your whole site.',
    accent: 'from-emerald-600 to-teal-500',
    count: '25+',
  },
  {
    id: 'templates',
    name: 'Templates',
    icon: LayoutTemplate,
    tagline: 'Beautiful, fast front-ends',
    description: 'Responsive Joomla 4, 5 & 6 templates with a visual builder, dark mode and Core Web Vitals baked in.',
    accent: 'from-amber-500 to-orange-500',
    count: '12+',
  },
];

// Featured extensions — loaded dynamically from the catalog API
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback accent colors keyed by extension_type
const ACCENT = { Component: 'text-blue-400', Module: 'text-purple-400', Plugin: 'text-emerald-400', Template: 'text-orange-400', Package: 'text-cyan-400' };

// Who our extensions are built for
const USE_CASES = [
  { name: 'E-commerce', icon: ShoppingCart },
  { name: 'Publishers & Blogs', icon: Newspaper },
  { name: 'Membership Sites', icon: Users },
  { name: 'Education', icon: GraduationCap },
  { name: 'Corporate', icon: Building2 },
  { name: 'Non-profits', icon: HeartHandshake },
  { name: 'Directories', icon: Package },
  { name: 'Agencies', icon: Sparkles },
];

// Why teams choose us
const WHY_US = [
  {
    title: 'Joomla 4, 5 & 6 Native',
    description: 'Every extension is written to the latest Joomla standards — namespaced code, the new MVC, Web Asset Manager and full multilingual support.',
    icon: Code2,
  },
  {
    title: 'Security-First Engineering',
    description: 'CSRF tokens, prepared statements, ACL-aware queries and regular audits. Our extensions pass the Joomla Vulnerable Extensions checks.',
    icon: Shield,
  },
  {
    title: 'Built for Performance',
    description: 'Lazy loading, smart caching and minimal queries keep your Core Web Vitals green even on shared hosting.',
    icon: Zap,
  },
];

const HomePage = () => {
  const videoRef = useRef(null);
  const [heroVideoUrl, setHeroVideoUrl] = useState(DEFAULT_HERO_VIDEO);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/builder/products?featured=true`)
      .then(r => r.json())
      .then(data => setFeaturedProducts(Array.isArray(data) ? data : []))
      .catch(() => setFeaturedProducts([]));
  }, []);

  useEffect(() => {
    setSeoMetadata({
      ...SEO_PRESETS.home,
      title: 'AI Joomla — Premium Joomla Extensions, Components, Modules, Plugins & Templates',
      description: 'AI Joomla builds premium Joomla 4, 5 & 6 extensions — components, modules, plugins and templates — plus custom extensions built to your spec on submission.',
      keywords: 'Joomla extensions, Joomla components, Joomla modules, Joomla plugins, Joomla templates, custom Joomla extension, Joomla 5, AI Joomla',
      jsonLd: [generateOrganizationSchema(), generateWebsiteSchema()],
    });
  }, []);

  // Load the Hero Overlay Video configured under Hero Media Assets so the
  // homepage hero background always matches the admin/dev setting.
  useEffect(() => {
    const fetchHeroVideo = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/settings/hero-display`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.hero_video_url) {
            setHeroVideoUrl(data.hero_video_url);
          }
        }
      } catch (e) {
        // Keep default video on failure
      }
    };
    fetchHeroVideo();
  }, []);

  // Reload the <video> element whenever the configured source changes.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [heroVideoUrl]);

  const scrollToCustom = () => {
    document.getElementById('custom-extension')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-bots-dark">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        {/* Hero Overlay Video background (from Hero Media Assets setting) */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
          data-testid="hero-video-background"
        >
          <source src={heroVideoUrl || DEFAULT_HERO_VIDEO} type={getVideoMimeType(heroVideoUrl || DEFAULT_HERO_VIDEO)} />
        </video>
        {/* Readability overlay + brand glow */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-bots-dark/85 via-bots-surface/70 to-bots-dark/90" />
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-blue-600/20 rounded-full blur-3xl z-[1]" />
        <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-purple-600/20 rounded-full blur-3xl z-[1]" />

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto py-24">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-6 animate-fade-in-down">
            <Sparkles className="w-4 h-4" /> Joomla 4, 5 &amp; 6 Native Extensions
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6 animate-fade-in">
            Premium Joomla Extensions,<br className="hidden md:block" /> Built by <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Our Team</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mb-10 animate-fade-in">
            Components, Modules, Plugins &amp; Templates engineered in-house — plus fully
            custom extensions built to your exact spec when you submit a request.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Link
              to="/shop"
              className="group px-8 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full text-lg hover:from-blue-500 hover:to-cyan-400 transition-all transform hover:scale-105 shadow-lg shadow-blue-500/30 w-full sm:w-auto"
              data-testid="hero-cta-browse"
            >
              <span className="flex items-center justify-center gap-2">
                <Package className="w-5 h-5" /> Browse Extensions
              </span>
            </Link>
            <Link
              to="/custom-extensions"
              className="group px-8 py-5 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full text-lg hover:from-purple-500 hover:to-indigo-400 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/30 w-full sm:w-auto"
              data-testid="hero-cta-custom"
            >
              <span className="flex items-center justify-center gap-2">
                <Code2 className="w-5 h-5" /> Request a Custom Build
              </span>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-10 text-gray-400 text-sm animate-fade-in">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Lifetime updates</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> GPL licensed</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Priority support</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-bots-surface border-y border-blue-500/10">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { k: '85+', v: 'Extensions shipped' },
            { k: '12k+', v: 'Sites powered' },
            { k: '4.9/5', v: 'Average rating' },
            { k: '24h', v: 'Support response' },
          ].map((s) => (
            <div key={s.v}>
              <div className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{s.k}</div>
              <div className="text-gray-400 text-sm mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Everything you need to extend Joomla</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Four families of extensions, all built and maintained by the AI Joomla team.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.id}
                  to="/shop"
                  className="group bg-bots-surface/60 border border-blue-500/15 rounded-2xl p-6 hover:border-blue-400/50 transition-all hover:-translate-y-1"
                  data-testid={`category-card-${c.id}`}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${c.accent} flex items-center justify-center mb-5 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-bold text-white">{c.name}</h3>
                    <span className="text-xs font-semibold text-blue-300 bg-blue-500/10 px-2 py-1 rounded-full">{c.count}</span>
                  </div>
                  <p className="text-blue-400 text-sm font-medium mb-3">{c.tagline}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{c.description}</p>
                  <span className="inline-flex items-center gap-1 text-blue-400 text-sm font-semibold mt-4 group-hover:gap-2 transition-all">
                    Explore {c.name} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured extensions */}
      <section className="py-20 bg-gradient-to-b from-bots-dark via-bots-surface to-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Featured extensions</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">A few of our most-loved releases. Every extension ships with docs, demos and a year of priority support.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.length > 0 ? featuredProducts.map((p) => {
              const accent = ACCENT[p.extension_type] || 'text-cyan-400';
              const href = p.landing_page_url || (p.seo_url ? `/extensions/${p.seo_url}` : '#');
              return (
                <Link
                  to={href}
                  key={p.id}
                  className="group bg-bots-surface/60 border border-blue-500/15 rounded-2xl p-5 hover:border-blue-400/50 transition-all hover:shadow-lg hover:shadow-blue-500/10 flex flex-col"
                  data-testid={`extension-card-${p.id}`}
                >
                  {p.box_image ? (
                    <img src={`${BACKEND_URL}${p.box_image}`} alt={p.name}
                      className="w-full h-32 object-cover rounded-xl mb-4 group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-4`}>
                      <Shield className={`w-6 h-6 ${accent}`} />
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${p.edition === 'PRO' ? 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10' : 'text-gray-300 border-white/10 bg-white/5'}`}>{p.edition || p.extension_type}</span>
                    <span className="text-xs text-gray-500">v{p.version}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">{p.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed flex-1">
                    {(p.short_description || p.description || '').replace(/<[^>]*>/g,'').substring(0,100).trim()}
                  </p>
                  <span className="mt-3 text-xs font-semibold text-blue-400 group-hover:text-blue-300 flex items-center gap-1">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            }) : (
              <div className="col-span-4 text-center text-gray-500 py-12">No featured extensions yet.</div>
            )}
          </div>
          <div className="text-center mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all shadow-lg shadow-blue-500/30">
              View the full catalog <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Engineered the right way</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">We build like a product team, not a marketplace reseller. Clean code, real support, and updates that keep pace with Joomla core.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_US.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-bots-surface/50 p-8 rounded-2xl border border-blue-500/20">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{f.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Updates / support banner */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-3 gap-8 text-white">
          <div className="flex items-start gap-4">
            <RefreshCw className="w-8 h-8 flex-shrink-0" />
            <div><h4 className="font-bold text-lg">Lifetime updates</h4><p className="text-blue-100 text-sm">Every purchase includes updates for the life of the product.</p></div>
          </div>
          <div className="flex items-start gap-4">
            <LifeBuoy className="w-8 h-8 flex-shrink-0" />
            <div><h4 className="font-bold text-lg">Real human support</h4><p className="text-blue-100 text-sm">Talk to the developers who actually built your extension.</p></div>
          </div>
          <div className="flex items-start gap-4">
            <Star className="w-8 h-8 flex-shrink-0" />
            <div><h4 className="font-bold text-lg">Loved by 12k+ sites</h4><p className="text-blue-100 text-sm">Rated 4.9/5 by developers and site owners worldwide.</p></div>
          </div>
        </div>
      </section>

      {/* Custom extension request */}
      <section id="custom-extension" className="py-20 bg-gradient-to-b from-bots-dark to-bots-surface">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-sm font-semibold mb-5">
                <Code2 className="w-4 h-4" /> Custom Extensions on Submission
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">Need something that doesn't exist yet?</h2>
              <p className="text-gray-300 text-lg mb-6">
                Submit your requirements and our team will scope, build and deliver a
                custom Joomla component, module, plugin or template — built to your
                brand, your data and your workflow.
              </p>
              <ul className="space-y-3">
                {['Free scoping & fixed-price quote', 'Built to Joomla 4, 5 & 6 standards', 'Source code + docs delivered', '30 days of post-launch support included'].map((i) => (
                  <li key={i} className="flex items-center text-white"><CheckCircle className="w-5 h-5 text-green-500 mr-3" />{i}</li>
                ))}
              </ul>
            </div>

            <div className="bg-bots-surface p-8 rounded-2xl border border-blue-500/20">
              <CustomExtensionForm />
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 bg-bots-dark">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Built for <span className="text-blue-400">every Joomla project</span>
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {USE_CASES.map((u, index) => {
              const Icon = u.icon;
              return (
                <div key={u.name} className="text-center group animate-fade-in-up" style={{ animationDelay: `${index * 40}ms` }}>
                  <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center bg-bots-surface/50 rounded-xl group-hover:bg-blue-500/20 transition-colors border border-blue-500/10">
                    <Icon className="w-7 h-7 text-blue-400" />
                  </div>
                  <p className="text-white text-sm font-medium">{u.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Custom Extension Request Form
const CustomExtensionForm = () => {
  const [formData, setFormData] = useState({
    company: '', firstName: '', lastName: '', email: '', phone: '',
    extensionType: '', budget: '', notes: '', agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone,
          subject: `Custom Extension Request - ${formData.company || 'Individual'}`,
          message: `Company: ${formData.company}\nExtension Type: ${formData.extensionType}\nBudget: ${formData.budget}\nProject Details: ${formData.notes}`,
          source: 'custom_extension',
        }),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', firstName: '', lastName: '', email: '', phone: '', extensionType: '', budget: '', notes: '', agreedToTerms: false });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">Request received!</h3>
        <p className="text-gray-300">Our team will review your requirements and reply within 24 hours with next steps and a quote.</p>
      </div>
    );
  }

  const inputCls = "w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="custom-extension-form">
      <input type="text" placeholder="Company / Project" value={formData.company}
        onChange={(e) => setFormData({ ...formData, company: e.target.value })} className={inputCls} data-testid="cx-company" />
      <div className="grid grid-cols-2 gap-4">
        <input type="text" placeholder="First Name*" required value={formData.firstName}
          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className={inputCls} data-testid="cx-firstname" />
        <input type="text" placeholder="Last Name*" required value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className={inputCls} data-testid="cx-lastname" />
      </div>
      <select required value={formData.extensionType}
        onChange={(e) => setFormData({ ...formData, extensionType: e.target.value })} className={inputCls} data-testid="cx-type">
        <option value="">What do you need built?*</option>
        <option value="Component">Component</option>
        <option value="Module">Module</option>
        <option value="Plugin">Plugin</option>
        <option value="Template">Template</option>
        <option value="Not sure yet">Not sure yet</option>
      </select>
      <select value={formData.budget}
        onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className={inputCls} data-testid="cx-budget">
        <option value="">Estimated budget (optional)</option>
        <option value="< $1,000">Under $1,000</option>
        <option value="$1,000 - $5,000">$1,000 – $5,000</option>
        <option value="$5,000 - $15,000">$5,000 – $15,000</option>
        <option value="$15,000+">$15,000+</option>
      </select>
      <div className="grid grid-cols-2 gap-4">
        <input type="email" placeholder="Email*" required value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} data-testid="cx-email" />
        <input type="tel" placeholder="Phone" value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} data-testid="cx-phone" />
      </div>
      <textarea placeholder="Describe what your extension should do*" required rows={4} value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className={`${inputCls} resize-none`} data-testid="cx-notes" />
      <label className="flex items-start text-gray-300 text-sm cursor-pointer">
        <input type="checkbox" required checked={formData.agreedToTerms}
          onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })} className="mr-3 mt-1" data-testid="cx-terms" />
        <span>I agree to be contacted by AI Joomla about my custom extension request.</span>
      </label>
      <button type="submit" disabled={isSubmitting}
        className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="cx-submit">
        {isSubmitting ? 'Submitting...' : 'Submit Request'}
      </button>
      {submitStatus === 'error' && <p className="text-red-400 text-center">Something went wrong. Please try again.</p>}
    </form>
  );
};

export default HomePage;
